/**
 * Types + title helper for manual-quote draft UI.
 * Draft persistence is server-only (`/quotes/drafts/*`). Legacy keys below are read once for migration.
 */
export const MANUAL_QUOTE_DRAFTS_LIST_KEY = "manual_quote_drafts_v1";
/** @deprecated legacy single-slot key */
export const MANUAL_QUOTE_DRAFT_LEGACY_KEY = "manual_quote_flow_draft";

export type ManualQuoteDraftStep = 1 | 2 | 3;

export type ManualQuoteDraftRecord = {
  id: string;
  savedAt: number;
  quotes: any[];
  step: ManualQuoteDraftStep;
  shippingData: { address_id: string; shipping_id: string };
  shippingMeta: {
    residence_type: "" | "residential" | "business" | "industrial";
    billing_same: boolean;
    delivery_instructions: string;
  };
  title?: string;
};

export function buildDraftTitleFromQuotes(quotes: any[] | undefined): string {
  const rows = Array.isArray(quotes) ? quotes : [];
  for (const q of rows) {
    const pn = String(q?.part_number ?? "").trim();
    if (pn) return pn.length > 56 ? `${pn.slice(0, 53)}…` : pn;
    const desc = String(q?.description ?? "").trim();
    if (desc) return desc.length > 56 ? `${desc.slice(0, 53)}…` : desc;
  }
  return "";
}

function parseJson<T>(raw: string | null): T | null {
  if (raw == null || raw === "") return null;
  try {
    return JSON.parse(raw) as T;
  } catch {
    return null;
  }
}

/** Read legacy browser drafts (no writes). Used for one-time migration to the server. */
export function readLegacyManualDraftRecords(): ManualQuoteDraftRecord[] {
  if (typeof window === "undefined") return [];
  const listRaw = parseJson<ManualQuoteDraftRecord[]>(window.localStorage.getItem(MANUAL_QUOTE_DRAFTS_LIST_KEY));
  const list = Array.isArray(listRaw) ? [...listRaw] : [];
  const legacy = parseJson<any>(window.localStorage.getItem(MANUAL_QUOTE_DRAFT_LEGACY_KEY));
  if (legacy?.quotes?.length) {
    const id =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : `mq-${Date.now()}`;
    list.push({
      id,
      savedAt: typeof legacy.savedAt === "number" ? legacy.savedAt : Date.now(),
      quotes: legacy.quotes,
      step: legacy.step === 2 || legacy.step === 3 ? legacy.step : 1,
      shippingData: legacy.shippingData ?? { address_id: "", shipping_id: "" },
      shippingMeta: legacy.shippingMeta ?? {
        residence_type: "",
        billing_same: false,
        delivery_instructions: "",
      },
      title: buildDraftTitleFromQuotes(legacy.quotes),
    });
  }
  return list;
}

export function clearLegacyManualQuoteDraftStorage(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(MANUAL_QUOTE_DRAFTS_LIST_KEY);
  window.localStorage.removeItem(MANUAL_QUOTE_DRAFT_LEGACY_KEY);
}

/** @deprecated Drafts are listed via `/quotes/drafts/list`; stub keeps legacy imports compiling. */
export function countManualQuoteDrafts(): number {
  return 0;
}
